import { schemaDefaultEmergencyContacts } from '../../../../api/defaults/links'
import React from 'react'
import { IDefaultEmergencyContact, TEmergencyContactSchema } from '../../../../api/types'
import { elWFull, FormLayout, InputGroup, InputWrapFull } from '@reapit/elements'
import { ErrorBoundary, strictInput } from '@reapitform/utils'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Select } from '@reapitform/common-ui'
import { contactTypeOptions } from './constanta'
import { useOrganizationContact } from '../../../../hooks/use-orgazination-contact'
import { parsePhoneNumber } from 'libphonenumber-js/max'

interface IEmergencyContactFormProps {
  defaultValues?: IDefaultEmergencyContact
  onSubmit: SubmitHandler<TEmergencyContactSchema>
  formId: string
}

export const EmergencyContactForm: React.FC<IEmergencyContactFormProps> = ({ formId, defaultValues, onSubmit }) => {
  const organizationInformation = useOrganizationContact()
  const phoneCountryCode = organizationInformation?.propertyAddress?.twoLetterISORegionName
  const emergencyContactSchema = schemaDefaultEmergencyContacts(phoneCountryCode)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TEmergencyContactSchema>({
    defaultValues: {
      useAsDefault: false,
      ...defaultValues,
    },
    resolver: yupResolver(emergencyContactSchema),
  })

  const submitHandler = (data: TEmergencyContactSchema) => {
    const payload: TEmergencyContactSchema = {
      ...data,
      phone: parsePhoneNumber(data.phone, phoneCountryCode).formatInternational(),
    }

    if (data.mobile) payload.mobile = parsePhoneNumber(data.mobile, phoneCountryCode).formatInternational()

    onSubmit?.(payload)
  }

  return (
    <ErrorBoundary>
      <form id={formId} onSubmit={handleSubmit(submitHandler)} className={elWFull}>
        <FormLayout hasMargin>
          <InputWrapFull>
            <Select
              errorMessage={errors.contactType?.message}
              options={contactTypeOptions}
              {...register('contactType')}
              label="Type *"
            />
          </InputWrapFull>
          <InputWrapFull>
            <InputGroup
              label="Business Name *"
              placeholder="Business Name"
              {...register('businessName')}
              errorMessage={errors.businessName?.message}
              maxLength={200}
            />
          </InputWrapFull>
          <InputWrapFull>
            <InputGroup
              label="Contact Name"
              placeholder="Contact Name"
              {...register('contactName')}
              errorMessage={errors.contactName?.message}
              maxLength={200}
            />
          </InputWrapFull>
          <InputWrapFull>
            <InputGroup
              label="Phone *"
              placeholder="Phone number"
              {...register('phone')}
              {...strictInput(/^[0-9+() ]*$/)}
              errorMessage={errors.phone?.message}
            />
          </InputWrapFull>
          <InputWrapFull>
            <InputGroup
              label="Mobile"
              placeholder="Mobile number"
              {...register('mobile')}
              {...strictInput(/^[0-9+() ]*$/)}
              errorMessage={errors.mobile?.message}
            />
          </InputWrapFull>
          <InputWrapFull>
            <InputGroup
              hasError={Boolean(errors.useAsDefault)}
              inputAddOnText="Add by default to all agreements"
              type="checkbox"
              checked={watch('useAsDefault')}
              onChange={() => setValue('useAsDefault', !watch('useAsDefault'))}
            />
          </InputWrapFull>
        </FormLayout>
      </form>
    </ErrorBoundary>
  )
}
